import React from 'react'
import qs from 'query-string'
import {
  LockIcon,
  NumberPadIcon,
  AddCashIcon,
  LocationIcon,
  FlagIcon,
  CreditMastercardIcon,
  DeleteIcon,
} from '@paypalcorp/pp-react'
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme'
import { hasFeature, serverData } from 'utils/server-data'
import {
  hasOperationMetadata,
  isDigitalOnlyCard,
  isRequestPhysicalCardWithFeeAllowed,
  shouldSetPin,
} from 'utils/card'
import { isCardDeactivatedOrLocked } from 'universal/lock'
import { OPERATIONS, SUB_OPERATIONS } from 'universal/cfs-card'
import { getUcsCardManagementPath } from 'universal/routes'
import { MANAGEMENT_ACTION_TABS, FLOWS } from 'universal/ucs'
import { REPLACE_CARD_REASONS } from '@/constants/card'

const { sysColorAccent1Main } = PAYPAL_THEME

const { APPLICATIONS, DEACTIVATE, SHOW_PIN, PINS, REPORT_CARD_LOST, REISSUE, CLOSE } = OPERATIONS
const {
  REACTIVATE,
  DEACTIVATE: SUB_DEACTIVATE,
  SHOW_PIN: SUB_SHOW_PIN,
  SET_PIN,
  REPLACE,
  WORN,
  REPORT_DIGITAL_CARD_LOST,
  CONVERT_FORM_FACTOR,
  CLOSE: SUB_CLOSE,
} = SUB_OPERATIONS
const { LOST_OR_STOLEN, GET_PHYSICAL_CARD } = REPLACE_CARD_REASONS

const iconStyle = { color: sysColorAccent1Main, margin: '0.85rem 1rem' }

export const getLockOrUnlockCardAction = (options) => {
  const { card } = options
  if (
    hasFeature('enableLockUnlockCard') &&
    ((isCardDeactivatedOrLocked(card.lifecycleState) &&
      hasOperationMetadata(card, { operation: APPLICATIONS, subOperation: REACTIVATE })
    ) ||
    (!isCardDeactivatedOrLocked(card.lifecycleState) && 
      hasOperationMetadata(card, { operation: DEACTIVATE, subOperation: SUB_DEACTIVATE })
    ))
  ) {
    return {
      icon: <LockIcon style={iconStyle} />,
      titleContentKey: 'lockCard.title',
      testId: 'lock-unlock-card',
    }
  }
  return null
}

export const getViewPinAction = (options) => {
  const { card, viewPinUrl } = options
  if (!hasOperationMetadata(card, { operation: SHOW_PIN, subOperation: SUB_SHOW_PIN })) {
    return null
  }
  return {
    icon: <NumberPadIcon style={iconStyle} />,
    titleContentKey: 'viewPin.title',
    actionPath: viewPinUrl,
    testId: 'view-pin',
  }
}

export const getChangePinAction = (options) => {
  const { card, changePinUrl } = options


  if (!hasOperationMetadata(card, { operation: PINS, subOperation: SET_PIN }) || (serverData?.locality?.country === "GB" && shouldSetPin(card, { shouldBeSetByUser: true, shouldCheckOfflinePin: false }))) { // eslint-disable-line i18n-scan/no-country-dependency -- GB-specific flow logic required for enrollment
    return null
  }
  return {
      icon: <NumberPadIcon style={iconStyle} />,
      titleContentKey: 'changePin.title',
      actionPath: changePinUrl,
      testId: 'change-pin',
  }
}

export const getAddCashAction = (options) => {
  const { actionUrls, showAddCash, card } = options
  const addCashAtStoreUrl = actionUrls?.addCashAtStore
  if (showAddCash && addCashAtStoreUrl) {
    const queryParams = qs.stringify({
      returnUri: getUcsCardManagementPath(card?.productName, {
        tab: MANAGEMENT_ACTION_TABS.MANAGE,
      }),
    })
    const addCashUrl = `${addCashAtStoreUrl}?${queryParams}`
    return {
      icon: <AddCashIcon style={iconStyle} />,
      titleContentKey: 'addCashAtStore.title',
      actionPath: addCashUrl,
      testId: 'add-cash-at-store',
    }
  }
  return null
}

export const getFindAtmAction = (options) => {
  const { actionUrls, card, showFindAtm } = options
  const findAtmLink = actionUrls?.findAtm
  if (isDigitalOnlyCard(card) || !showFindAtm || !findAtmLink) {
    return null
  }
  return {
    icon: <LocationIcon style={iconStyle} />,
    titleContentKey: 'findATM.title',
    actionPath: findAtmLink,
    pathToOpenInNewTab: true,
    testId: 'find-atm',
  }
}

export const getReportLostOrDamagedAction = (options) => {
  const { card, replaceCardUrl } = options
  if (
    isDigitalOnlyCard(card) &&
    hasOperationMetadata(card, {
      operation: REPORT_CARD_LOST,
      subOperation: REPORT_DIGITAL_CARD_LOST,
    })
  ) {
    // CCLS will return both REPORT_CARD_LOST and REPORT_DIGITAL_CARD_LOST suboperations for Digital Only Card
    // Since the report lost options are mutually exclusive, we should only display the best option for Digital Only Card
    return null
  }

  const titleContentKey = getReportLostOrDamagedContentKey()
  if (!titleContentKey) {
    return null
  }
  return {
    icon: <FlagIcon style={iconStyle} />,
    titleContentKey,
    actionPath: replaceCardUrl,
    testId: 'replace-card',
  }

  function getReportLostOrDamagedContentKey() {
    const isReportLostAllowed = hasOperationMetadata(card, {
      operation: REPORT_CARD_LOST,
      subOperation: REPLACE,
    })

    const isReplaceDamagedAllowed = hasOperationMetadata(card, {
      operation: REISSUE,
      subOperation: WORN,
    })

    let contentKey = ''
    if (isReportLostAllowed && isReplaceDamagedAllowed) {
      contentKey = 'reportLostStolenDmg.title'
    } else if (isReportLostAllowed) {
      contentKey = 'reportLostStolen.title'
    }
    return contentKey
  }
}

export const getLostOrDamagedDigitalAction = (options) => {
  const { card, replaceCardUrl } = options
  if (
    !hasOperationMetadata(card, {
      operation: REPORT_CARD_LOST,
      subOperation: REPORT_DIGITAL_CARD_LOST,
    })
  ) {
    return null
  }
  return {
    icon: <FlagIcon style={iconStyle} />,
    titleContentKey: 'reportStolenDigitalCard',
    actionPath: `${replaceCardUrl}&reason=${LOST_OR_STOLEN}`,
    testId: 'replace-card-digital',
  }
}

export const getPhysicalCardAction = (options) => {
  // TODO: To refactor request physical card flow to support both US and DE
  const { card, replaceCardUrl, requestPhysicalCardUrl } = options
  const convertWithFee = isRequestPhysicalCardWithFeeAllowed(card, FLOWS.MANAGE_CARD)
  if (
    !hasOperationMetadata(card, { operation: REISSUE, subOperation: CONVERT_FORM_FACTOR }) &&
    !convertWithFee
  ) {
    return null
  }
  const actionLink = convertWithFee
    ? requestPhysicalCardUrl
    : replaceCardUrl + '&reason=' + GET_PHYSICAL_CARD
  return {
    icon: <CreditMastercardIcon style={iconStyle} />,
    titleContentKey: 'getPhysicalCard',
    actionPath: actionLink,
    testId: 'get-physical-card',
  }
}

export const getCancelCardAction = (options) => {
  const { card, cancelCardUrl } = options
  if (!hasOperationMetadata(card, { operation: CLOSE, subOperation: SUB_CLOSE })) {
    return null
  }
  return {
    icon: <DeleteIcon style={iconStyle} />,
    titleContentKey: 'cancelCard.title',
    actionPath: cancelCardUrl,
    testId: 'cancel-card',
  }
}

export const getCreatePinAction = (options) => {
  const { card, createPinURL, showCreatePin } = options
  if (!showCreatePin || (!hasOperationMetadata(card, { operation: PINS, subOperation: SET_PIN }) || !shouldSetPin(card, { shouldBeSetByUser: true, shouldCheckOfflinePin: false }))) {
    return null
  }
  return {
    icon: <NumberPadIcon style={iconStyle} />,
    titleContentKey: 'createPin.title',
    actionPath: createPinURL,
    testId: 'create-pin',
  }
}